import React, { type ReactNode } from 'react';
import {
  Button,
  ExternalActivityListener,
  GetColor,
  Icon,
  portalMenuIgnoreActivityClassName,
  RelativePortal,
  ShimmerBlock,
  Tooltip,
  ZIndex,
} from 'venn-ui-kit';
import { Input } from '../input';
import styled, { css } from 'styled-components';
import { useHasFF } from 'venn-utils';
import { cn } from '@/lib/utils';

interface InputEditorProps {
  title: ReactNode;
  name: string;
  inputTypeName: string;
  setName: (name: string) => void;
  onClose: () => void;
  onSave: () => void;
  onDelete: (() => void) | undefined;
  isNew?: boolean;
  children: React.ReactNode;
  disabled?: boolean;
  maxHeight?: number;
  maxSelectionsReached?: boolean;
  warningSelectionsReached?: boolean;
  ref?: React.RefObject<HTMLDivElement>;
}

export const InputEditor = ({
  name,
  inputTypeName,
  setName,
  onClose,
  onSave,
  onDelete,
  isNew,
  children,
  title,
  disabled,
  maxHeight,
  maxSelectionsReached,
}: InputEditorProps) => {
  const hasBlockAssignment = useHasFF('block_assignment_ff');

  return (
    <RelativePortal expectedHeight={maxHeight} style={{ zIndex: ZIndex.Cover }}>
      <ExternalActivityListener
        ignoreActivityFromClassName={portalMenuIgnoreActivityClassName}
        onExternalActivity={onClose}
      >
        <Container hasNewStyle={hasBlockAssignment}>
          <Title>
            {title}
            <button type="button" aria-label="Close dialog" onClick={onClose}>
              <Icon
                className={cn({
                  'text-venn-primary-dark': !hasBlockAssignment,
                  'text-venn-dark-blue': hasBlockAssignment,
                })}
                type="times"
              />
            </button>
          </Title>
          {hasBlockAssignment && (
            <div className="flex flex-col px-[20px] pb-[16px] gap-[4px]">
              <b id="input-label">{inputTypeName} name</b>
              <Input
                value={name}
                onChange={setName}
                placeholder="Missing name"
                aria-labelledby="input-label"
                refreshedStyling
              />
            </div>
          )}
          {children}
          <Footer hasNewStyle={hasBlockAssignment}>
            {hasBlockAssignment ? (
              onDelete ? (
                <Button refreshedStyling disabled={disabled} destructive noMargin onClick={onDelete}>
                  Delete {inputTypeName}
                </Button>
              ) : null
            ) : (
              <GroupName>
                <b id="input-label">Input Name</b>
                <Input
                  value={name}
                  onChange={setName}
                  placeholder="Missing name"
                  style={{ width: '300px', height: '35px' }}
                  aria-labelledby="input-label"
                />
              </GroupName>
            )}
            <Tooltip
              isHidden={!disabled || !maxSelectionsReached}
              content={maxSelectionsReached ? 'Maximum number of selections reached' : undefined}
            >
              <Button refreshedStyling={hasBlockAssignment} disabled={disabled} dominant noMargin onClick={onSave}>
                {hasBlockAssignment ? 'Apply' : isNew ? 'Add Selected' : 'Save Changes'}
              </Button>
            </Tooltip>
          </Footer>
        </Container>
      </ExternalActivityListener>
    </RelativePortal>
  );
};

export const InputEditorLoadState = ({ maxHeight }: Pick<InputEditorProps, 'maxHeight'>) => {
  const hasBlockAssignment = useHasFF('block_assignment_ff');
  return (
    <RelativePortal expectedHeight={maxHeight} style={{ zIndex: ZIndex.Cover }}>
      <Container hasNewStyle={hasBlockAssignment}>
        <ShimmerBlock height={maxHeight} margin={0} />
      </Container>
    </RelativePortal>
  );
};

const GroupName = styled.div`
  display: flex;
  flex-direction: column;
`;

const SpaceBetween = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 20px;
`;

const Title = styled(SpaceBetween)`
  padding-bottom: 20px;
  align-items: center;
  font-size: 14px;
  font-weight: bold;
`;

const Footer = styled.div<{ hasNewStyle: boolean }>`
  display: flex;
  justify-content: space-between;
  ${({ hasNewStyle }) =>
    hasNewStyle
      ? css`
          border-top: 1px solid ${GetColor.GreyScale.Grey30};
          padding: 12px 20px;
        `
      : css`
          padding: 0 20px;
          box-shadow: 0 -4px 4px rgba(0, 0, 0, 0.1);
          align-items: flex-end;
          height: 80px;
        `}
`;

const Container = styled.div<{ hasNewStyle: boolean }>`
  z-index: ${ZIndex.Front};
  padding: ${({ hasNewStyle }) => (hasNewStyle ? '20px 0 0 0' : '20px 0')};
  border: 4px solid ${({ hasNewStyle }) => (hasNewStyle ? GetColor.DarkBlue : GetColor.Primary.Main)};
  box-shadow: 0 2px 2px rgba(0, 0, 0, 0.12);
  background-color: ${GetColor.White};
  width: 635px;
  display: flex;
  flex-direction: column;
`;
